import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { BrandingConfig, DEFAULT_BRANDING } from '@photobooth/types';
import { loadBranding, saveBranding } from './storage';
import { applyBrandingToDOM } from './theme';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../config/terminal-urls';

interface BrandingContextValue {
  branding: BrandingConfig;
  isLoading: boolean;
  updateBranding: (branding: Partial<BrandingConfig>) => Promise<void>;
  resetBranding: () => Promise<void>;
  fetchOutletBranding: (outletId: string) => Promise<void>;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

interface BrandingProviderProps {
  children: ReactNode;
  outletId?: string | null;
}

export function BrandingProvider({ children, outletId }: BrandingProviderProps) {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [apiClient] = useState(() => {
    return createPhotoBoothAPI({
      baseUrl: getEffectiveApiBaseUrl(),
    });
  });

  // Load branding on mount or when outletId changes
  useEffect(() => {
    const loadBrandingData = async () => {
      try {
        setIsLoading(true);
        
        if (outletId) {
          // Try to fetch outlet-specific branding
          await fetchOutletBrandingInternal(outletId);
        } else {
          // Load cached or default branding
          const loadedBranding = await loadBranding();
          setBranding(loadedBranding);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load branding:', error);
        setBranding(DEFAULT_BRANDING);
        setIsLoading(false);
      }
    };

    loadBrandingData();
  }, [outletId]);

  // Apply branding to DOM whenever it changes
  useEffect(() => {
    if (!isLoading) {
      applyBrandingToDOM(branding);
    }
  }, [branding, isLoading]);

  /**
   * Fetch outlet-specific branding from API
   * Falls back to global branding if outlet branding is not available
   */
  const fetchOutletBrandingInternal = async (outletId: string): Promise<void> => {
    try {
      // First, try to get outlet-specific branding
      const outletResponse = await apiClient.outlets.getById(outletId);
      
      if (outletResponse.success && outletResponse.data?.configuration) {
        const config = outletResponse.data.configuration as any;
        
        // Check if outlet has custom branding in configuration
        if (config.branding) {
          // Merge outlet branding with default branding
          const outletBranding = { ...DEFAULT_BRANDING, ...config.branding };
          await saveBranding(outletBranding);
          setBranding(outletBranding);
          console.info('Loaded outlet-specific branding for outlet:', outletId);
          return;
        }
      }
      
      // Fallback: Fetch global branding
      console.info('No outlet-specific branding found, using global branding');
      const globalBranding = await apiClient.branding.getBranding();
      await saveBranding(globalBranding);
      setBranding(globalBranding);
    } catch (error) {
      console.error('Failed to fetch outlet branding, using cached or default:', error);
      
      // Try to load from cache
      const cachedBranding = await loadBranding();
      setBranding(cachedBranding);
    }
  };

  const fetchOutletBranding = async (outletId: string): Promise<void> => {
    setIsLoading(true);
    try {
      await fetchOutletBrandingInternal(outletId);
    } finally {
      setIsLoading(false);
    }
  };

  const updateBranding = async (updates: Partial<BrandingConfig>) => {
    const updated = { ...branding, ...updates };
    await saveBranding(updated);
    setBranding(updated);
  };

  const resetBranding = async () => {
    await saveBranding(DEFAULT_BRANDING);
    setBranding(DEFAULT_BRANDING);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📸</div>
          <div className="text-xl font-bold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <BrandingContext.Provider value={{ branding, isLoading, updateBranding, resetBranding, fetchOutletBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
}
